import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "@/lib/firebase";
import { collection, getDocs, query, where, updateDoc, doc, getDoc, onSnapshot, addDoc, Timestamp } from "firebase/firestore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const StaffLicenseRequests: React.FC = () => {
  const [branch, setBranch] = useState<string | null>(null);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [licenseRequests, setLicenseRequests] = useState<any[]>([]);
  const [licenseCodes, setLicenseCodes] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>("");

  // Filters
  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [month, setMonth] = useState<string>("all");
  const [year, setYear] = useState<string>("all");

  const statusOptions = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];
  
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  };

  // Filtered license requests
  const filteredRequests = useMemo(() => {
    return licenseRequests.filter(request => {
      const student = students.find(s => s.id === request.studentId);
      const licenseCode = licenseCodes.find(lc => request.licenseCodes?.includes(lc.id));
      // Date filter
      const dateField = request.requestedAt;
      let createdAt = dateField ? new Date(dateField.seconds * 1000) : null;
      const matchesStatus = status === "all" || request.status === status;
      const matchesMonth = month === "all" || (createdAt && createdAt.getMonth() + 1 === Number(month));
      const matchesYear = year === "all" || (createdAt && createdAt.getFullYear() === Number(year));
      const studentName = student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "";
      const matchesSearch =
        !search ||
        (student &&
          ((studentName && studentName.toLowerCase().includes(search.toLowerCase())) ||
            (student.email && student.email.toLowerCase().includes(search.toLowerCase())))) ||
        (licenseCode && ((licenseCode.code && licenseCode.code.toLowerCase().includes(search.toLowerCase())) || (licenseCode.description && licenseCode.description.toLowerCase().includes(search.toLowerCase()))));
      return matchesStatus && matchesMonth && matchesYear && matchesSearch;
    });
  }, [licenseRequests, students, licenseCodes, status, month, year, search]);

  useEffect(() => {
    if (!auth.currentUser) return;

    const fetchBranchAndSetupListeners = async () => {
      setLoading(true);
      const unsubscribers: (() => void)[] = [];

      try {
        // Fetch staff user profile to get branch using document ID
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        const userData = userDoc.data();
        
        if (userData && userData.branch) {
          setBranchId(userData.branch);
          
          // Fetch branch name from branches collection
          const branchDoc = await getDoc(doc(db, "branches", userData.branch));
          const branchData = branchDoc.data();
          setBranch(branchData?.name || userData.branch);
          
          // Set up real-time listener for license code requests
          const requestsUnsubscribe = onSnapshot(
            query(collection(db, "licenseCodeRequests"), where("branch", "==", userData.branch)),
            (snapshot) => {
              setLicenseRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            },
            (error) => {
              console.error("Error listening to license requests:", error);
              setMessage("Error loading license requests. Please refresh.");
            }
          );
          unsubscribers.push(requestsUnsubscribe);
          
          // Set up real-time listener for license codes
          const licenseCodesUnsubscribe = onSnapshot(collection(db, "licenseCodes"), (snapshot) => {
            setLicenseCodes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          });
          unsubscribers.push(licenseCodesUnsubscribe);
          
          // Set up real-time listener for students
          const studentsUnsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
            setStudents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          });
          unsubscribers.push(studentsUnsubscribe);
        }
      } catch (error) {
        console.error("Error setting up license requests:", error);
        setMessage("Error loading data. Please refresh.");
      } finally {
        setLoading(false);
      }

      // Cleanup function
      return () => {
        unsubscribers.forEach(unsubscribe => unsubscribe());
      };
    };

    const cleanup = fetchBranchAndSetupListeners();
    
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
    };
  }, []);

  const updateStatus = async (requestId: string, newStatus: string, staffNotes?: string) => {
    try {
      const updateData: any = { 
        status: newStatus,
        updatedAt: Timestamp.now(),
        updatedBy: auth.currentUser?.uid
      };
      
      if (staffNotes) {
        updateData.staffNotes = staffNotes;
      }
      
      await updateDoc(doc(db, "licenseCodeRequests", requestId), updateData);
      
      // Create notification for student
      const request = licenseRequests.find(r => r.id === requestId);
      if (request) {
        const licenseCode = licenseCodes.find(lc => request.licenseCodes?.includes(lc.id));
        const notificationTitle = newStatus === "approved" ? "License Code Request Approved" : "License Code Request Update";
        const notificationMessage = newStatus === "approved" 
          ? `Your license code request for ${licenseCode?.code || "unknown code"} has been approved!`
          : `Your license code request for ${licenseCode?.code || "unknown code"} has been ${newStatus}.${staffNotes ? ` Note: ${staffNotes}` : ""}`;
        
        await addDoc(collection(db, "notifications"), {
          recipientId: request.studentId,
          type: "license_code_update",
          title: notificationTitle,
          message: notificationMessage,
          isRead: false,
          createdAt: Timestamp.now(),
          relatedId: requestId,
        });
      }
      
      setMessage(`License code request ${newStatus} successfully`);
      // Clear message after 3 seconds
      setTimeout(() => setMessage(""), 3000);
    } catch (err: any) {
      setMessage("Failed to update status: " + err.message);
      setTimeout(() => setMessage(""), 5000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">License Code Requests</h1>
        {branch && (
          <div className="text-sm text-muted-foreground">
            Branch: {branch}
          </div>
        )}
      </div>
      
      {message && <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">{message}</div>}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="text-lg">Loading...</div>
        </div>
      ) : !branch ? (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          No branch assigned to your account.
        </div>
      ) : (
        <>
          {/* Filters - Single row layout consistent with other modules */}
          <div className="flex flex-wrap gap-4 mb-6 items-end">
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium">Status</label>
              <select className="w-32 border rounded px-2 py-1" value={status} onChange={e => setStatus(e.target.value)}>
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium">Month</label>
              <select className="w-28 border rounded px-2 py-1" value={month} onChange={e => setMonth(e.target.value)}>
                <option value="all">All</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{new Date(2020, i, 1).toLocaleString('default', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium">Year</label>
              <select className="w-24 border rounded px-2 py-1" value={year} onChange={e => setYear(e.target.value)}>
                <option value="all">All</option>
                {getYearOptions().map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col flex-1 min-w-64">
              <label className="mb-1 text-sm font-medium">Search</label>
              <input
                className="border rounded px-2 py-1"
                placeholder="Search by student, email, or code"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>License Code Requests ({filteredRequests.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No license code requests found for the selected filters.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-muted">
                        <th className="px-4 py-2 text-left border font-medium">Student</th>
                        <th className="px-4 py-2 text-left border font-medium">License Code</th>
                        <th className="px-4 py-2 text-left border font-medium">Price</th>
                        <th className="px-4 py-2 text-left border font-medium">Status</th>
                        <th className="px-4 py-2 text-left border font-medium">Payment Proof</th>
                        <th className="px-4 py-2 text-left border font-medium">Requested Date</th>
                        <th className="px-4 py-2 text-left border font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map(request => {
                        const student = students.find(s => s.id === request.studentId);
                        const licenseCode = licenseCodes.find(lc => request.licenseCodes?.includes(lc.id));
                        
                        return (
                          <tr key={request.id} className="hover:bg-muted/50">
                            <td className="px-4 py-2 border">
                              <div className="font-medium">
                                {student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : request.studentId}
                              </div>
                              {student && (
                                <div className="text-sm text-muted-foreground">{student.email}</div>
                              )}
                            </td>
                            <td className="px-4 py-2 border">
                              <div className="font-medium">{licenseCode?.code || 'Unknown'}</div>
                              {licenseCode && (
                                <div className="text-sm text-muted-foreground">{licenseCode.description}</div>
                              )}
                            </td>
                            <td className="px-4 py-2 border">
                              <span className="font-medium">₱{request.price || licenseCode?.price || 0}</span>
                            </td>
                            <td className="px-4 py-2 border">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                request.status === "approved" ? "bg-green-100 text-green-800" :
                                request.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                                request.status === "rejected" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"
                              }`}>
                                {request.status === "pending" ? "Pending" : 
                                 request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              </span>
                              {request.status === "rejected" && request.staffNotes && (
                                <div className="mt-1 text-xs text-red-600">
                                  Reason: {request.staffNotes}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-2 border">
                              <div className="space-y-2">
                                <div className="flex space-x-2">
                                  {request.paymentProof ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => window.open(request.paymentProof, '_blank')}
                                      className="flex-1"
                                    >
                                      View Receipt
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled
                                      className="flex-1"
                                    >
                                      No Receipt
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-2 border">
                              <div className="text-sm">
                                {request.requestedAt ? new Date(request.requestedAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {request.requestedAt ? new Date(request.requestedAt.seconds * 1000).toLocaleTimeString() : ''}
                              </div>
                            </td>
                            <td className="px-4 py-2 border">
                              {request.status === "pending" && (
                                <div className="space-y-2">
                                  <div className="flex space-x-2">
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700 text-white flex-1"
                                      onClick={() => updateStatus(request.id, "approved")}
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="flex-1"
                                      onClick={() => {
                                        const reason = prompt("Reason for rejection (optional):");
                                        updateStatus(request.id, "rejected", reason || undefined);
                                      }}
                                    >
                                      Reject
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {request.status === "approved" && (
                                <div className="text-center">
                                  <div className="text-xs text-green-600">✓ Approved</div>
                                  <div className="text-xs text-gray-500 mt-1">Request Completed</div>
                                </div>
                              )}
                              {request.status === "rejected" && (
                                <div className="text-center">
                                  <div className="text-xs text-red-600">✗ Rejected</div>
                                  <div className="text-xs text-gray-500 mt-1">Request Declined</div>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default StaffLicenseRequests;
